<template>
  <div class="dealer-about">
    <!-- Header Section -->
    <section class="about-hero">
      <v-container>
        <div class="hero-content">
          <h1 class="hero-title">About AutoSyncX™</h1>
          <p class="hero-subtitle">
            Powering Dealers. Empowering Agents. Driving Sales.
          </p>
          <p class="hero-description">
            AutoSyncX™ was built with one mission: to bridge the gap between dealerships, 
            referral agents, and buyers through automation, transparency, and trust.
          </p>
          <p class="hero-belief">
            We believe dealerships should focus on selling cars, agents should focus on 
            connecting with buyers, and technology should handle the rest.
          </p>
        </div>
      </v-container>
    </section>

    <!-- Our Story Section -->
    <section class="story-section">
      <v-container>
        <div class="section-header">
          <div class="section-divider">⸻</div>
          <h2 class="section-title">Our Story</h2>
        </div>
        <div class="story-content">
          <p class="story-text">
            Founded by entrepreneurs and technologists who understand both automotive sales 
            and digital transformation, AutoSyncX™ was designed to give dealers a competitive 
            edge and agents the tools to succeed in today's fast-changing market.
          </p>
        </div>
      </v-container>
    </section>

    <!-- What We Do Section -->
    <section class="services-section">
      <v-container>
        <div class="section-header">
          <div class="section-divider">⸻</div>
          <h2 class="section-title">What We Do</h2>
        </div>
        <div class="services-grid">
          <div class="service-item" v-for="service in services" :key="service.id">
            <div class="service-icon">
              <i class="material-icons-round">{{ service.icon }}</i>
            </div>
            <h3 class="service-title">{{ service.title }}</h3>
            <p class="service-description">{{ service.description }}</p>
          </div>
        </div>
      </v-container>
    </section>

    <!-- Vision Section -->
    <section class="vision-section">
      <v-container>
        <div class="section-header">
          <div class="section-divider">⸻</div>
          <h2 class="section-title">Our Vision</h2>
        </div>
        <div class="vision-content">
          <p class="vision-text">
            To become the #1 referral-powered automotive platform, where dealers grow, 
            agents earn, and buyers win.
          </p>
        </div>
      </v-container>
    </section>

    <!-- Values Section -->
    <section class="values-section">
      <v-container>
        <div class="section-header">
          <div class="section-divider">⸻</div>
          <h2 class="section-title">Our Values</h2>
        </div>
        <div class="values-grid">
          <div class="value-item" v-for="value in values" :key="value.id">
            <div class="value-icon">{{ value.emoji }}</div>
            <h3 class="value-title">{{ value.title }}</h3>
            <p class="value-description">{{ value.description }}</p>
          </div>
        </div>
      </v-container>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <v-container>
        <div class="cta-content">
          <h2 class="cta-title">Ready to Transform Your Dealership?</h2>
          <p class="cta-subtitle">
            Join AutoSyncX™ and connect with thousands of referral agents today.
          </p>
          <v-btn
            class="cta-btn"
            large
            dark
            @click="signUpDealership"
          >
            Get Started Today
          </v-btn>
        </div>
      </v-container>
    </section>
  </div>
</template>

<script>
export default {
  name: "DealerAbout",
  data() {
    return {
      services: [
        {
          id: 1,
          icon: 'track_changes',
          title: 'Seamless Commission Tracking',
          description: 'Dealers set commissions, agents share cars, buyers close deals.'
        },
        {
          id: 2,
          icon: 'groups',
          title: 'Nationwide Agent Network',
          description: 'Tap into 1,000s of trained agents instantly.'
        },
        {
          id: 3,
          icon: 'sync',
          title: 'Automated Inventory Sync',
          description: 'API & scraping integrations keep listings up to date.'
        },
        {
          id: 4,
          icon: 'payments',
          title: 'Transparent Payouts',
          description: 'Every commission is clear, visible, and paid out fast.'
        }
      ],
      values: [
        {
          id: 1,
          emoji: '🚗',
          title: 'Innovation First',
          description: 'Always building smarter tools.'
        },
        {
          id: 2,
          emoji: '🤝',
          title: 'Partnership',
          description: 'Success only works when we all win together.'
        },
        {
          id: 3,
          emoji: '📊',
          title: 'Transparency',
          description: 'No hidden fees, no games, just clear commissions.'
        },
        {
          id: 4,
          emoji: '🌍',
          title: 'Scalability',
          description: 'Whether 1 dealership or 1,000, AutoSyncX™ grows with you.'
        }
      ]
    }
  },
  methods: {
    signUpDealership() {
      // Navigate to dealer signup and scroll to top
      this.$router.push('/dealers/signup');
      // Scroll to top after navigation
      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  },
  mounted() {
    // Scroll to top when component is mounted
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
</script>

<style scoped>
/* About Page Styles */
.dealer-about {
  font-family: 'Inter', sans-serif;
}

/* Hero Section */
.about-hero {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 120px 0 80px;
  text-align: center;
}

.hero-content {
  max-width: 800px;
  margin: 0 auto;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 24px;
  line-height: 1.2;
}

.hero-subtitle {
  font-size: 1.5rem;
  font-weight: 600;
  color: #007bff;
  margin-bottom: 32px;
  line-height: 1.4;
}

.hero-description {
  font-size: 1.2rem;
  color: #4a5568;
  margin-bottom: 24px;
  line-height: 1.6;
}

.hero-belief {
  font-size: 1.1rem;
  color: #6b7280;
  font-style: italic;
  line-height: 1.6;
}

/* Section Styles */
.story-section,
.services-section,
.vision-section,
.values-section {
  padding: 80px 0;
}

.story-section {
  background: #ffffff;
}

.services-section {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.vision-section {
  background: #ffffff;
}

.values-section {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-divider {
  font-size: 1.5rem;
  color: #007bff;
  margin-bottom: 20px;
  font-weight: 300;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0;
}

/* Story Section */
.story-content {
  max-width: 700px;
  margin: 0 auto;
  text-align: center;
}

.story-text {
  font-size: 1.2rem;
  color: #4a5568;
  line-height: 1.7;
}

/* Services Section */
.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
}

.service-item {
  text-align: center;
  padding: 40px 20px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.service-item:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.service-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
}

.service-icon i {
  font-size: 2rem;
  color: white;
}

.service-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 16px;
}

.service-description {
  font-size: 1rem;
  color: #6b7280;
  line-height: 1.6;
}

/* Vision Section */
.vision-content {
  max-width: 700px;
  margin: 0 auto;
  text-align: center;
}

.vision-text {
  font-size: 1.3rem;
  color: #4a5568;
  line-height: 1.7;
  font-weight: 500;
}

/* Values Section */
.values-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  max-width: 1000px;
  margin: 0 auto;
}

.value-item {
  text-align: center;
  padding: 30px 20px;
}

.value-icon {
  font-size: 3rem;
  margin-bottom: 20px;
}

.value-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 12px;
}

.value-description {
  font-size: 1rem;
  color: #6b7280;
  line-height: 1.6;
}

/* CTA Section */
.cta-section {
  background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
  padding: 100px 0;
  text-align: center;
}

.cta-content {
  max-width: 600px;
  margin: 0 auto;
}

.cta-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 20px;
  line-height: 1.3;
}

.cta-subtitle {
  font-size: 1.2rem;
  color: #cbd5e0;
  margin-bottom: 40px;
  line-height: 1.6;
}

.cta-btn {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%) !important;
  color: white !important;
  padding: 16px 40px !important;
  font-size: 1.1rem !important;
  font-weight: 700 !important;
  border-radius: 50px !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 8px 25px rgba(0, 123, 255, 0.3) !important;
  transition: all 0.3s ease !important;
}

.cta-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 12px 35px rgba(0, 123, 255, 0.4) !important;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }
  
  .hero-subtitle {
    font-size: 1.2rem;
  }
  
  .hero-description {
    font-size: 1.1rem;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .services-grid,
  .values-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  
  .cta-title {
    font-size: 2rem;
  }
  
  .story-section,
  .services-section,
  .vision-section,
  .values-section {
    padding: 60px 0;
  }
  
  .about-hero {
    padding: 80px 0 60px;
  }
  
  .cta-section {
    padding: 80px 0;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 2rem;
  }
  
  .hero-subtitle {
    font-size: 1.1rem;
  }
  
  .section-title {
    font-size: 1.8rem;
  }
  
  .cta-title {
    font-size: 1.8rem;
  }
  
  .service-item {
    padding: 30px 15px;
  }
}
</style>
